/**
 * Genera el tablero físico del plinko a partir de un `BoardDef` (ya resuelto
 * por `plinko/logic/boardMods.applyBoardMods`): barras estáticas con
 * sensor, paredes de contención, embudo final y vaso receptor.
 *
 * EXCEPCIÓN DELIBERADA a la regla "sin Phaser en plinko/logic": este archivo
 * construye cuerpos Matter y sprites, así que sí importa Phaser. La
 * matemática (cuánto multiplica cada barra, oscilación de las móviles, ...)
 * vive en `plinko/logic/*` y se testea aparte.
 */

import Phaser from 'phaser';
import { TEX, multBarColor } from '../core/TextureFactory';
import type { BarKind } from '../core/types';
import type { BoardDef } from '../core/types';
import { barId } from './logic/mysteryReveal';
import { movingBarOffsetX } from './logic/movingBar';
import type { BarMeta, PlinkoRect } from './types';

/** Alto fijo (px) de cada barra, y grosor de las paredes de contención. */
const BAR_HEIGHT_PX = 16;
const WALL_THICKNESS_PX = 10;
/** Fracción del alto de la zona reservada al embudo + vaso receptor, al pie del tablero. */
const FUNNEL_HEIGHT_FRAC = 0.16;

function textureForKind(kind: BarKind): string {
  switch (kind) {
    case 'mult':
      return TEX.barMult;
    case 'mystery':
      return TEX.barMystery;
    case 'trampoline':
      return TEX.barTrampoline;
    case 'negative':
      return TEX.barNegative;
    default: {
      const exhaustive: never = kind;
      throw new Error(`textureForKind: tipo de barra desconocido '${String(exhaustive)}'`);
    }
  }
}

interface BarRuntime {
  bodyId: number;
  meta: BarMeta;
  image: Phaser.Physics.Matter.Image;
  label?: Phaser.GameObjects.Text;
  pixelY: number;
}

export class Board {
  /** Área jugable de las barras (excluye la franja del embudo, al pie). */
  readonly playRect: PlinkoRect;
  private funnelBody: MatterJS.BodyType | undefined;
  private funnelBodyId: number | undefined;
  private wallBodies: MatterJS.BodyType[] = [];
  private wallBodyIds = new Set<number>();
  private bars = new Map<number, BarRuntime>();
  private movingBars: BarRuntime[] = [];
  private receiverImage?: Phaser.GameObjects.Image;
  private decorGraphics?: Phaser.GameObjects.Graphics;

  constructor(
    private readonly scene: Phaser.Scene,
    readonly zoneRect: PlinkoRect,
    boardDef: BoardDef,
    /** Acento de bioma (ver `core/biomePalette.ts`) para las líneas del embudo y el vaso receptor. */
    private readonly accentColor: number = 0x2c6ea3
  ) {
    const funnelHeight = zoneRect.height * FUNNEL_HEIGHT_FRAC;
    this.playRect = {
      x: zoneRect.x,
      y: zoneRect.y,
      width: zoneRect.width,
      height: zoneRect.height - funnelHeight,
    };

    this.buildWalls();
    this.buildBars(boardDef);
    this.buildFunnel(funnelHeight);
  }

  private buildWalls(): void {
    const { x, y, width, height } = this.zoneRect;
    const leftWall = this.scene.matter.add.rectangle(
      x - WALL_THICKNESS_PX / 2,
      y + height / 2,
      WALL_THICKNESS_PX,
      height,
      { isStatic: true, label: 'plinko-wall' }
    );
    const rightWall = this.scene.matter.add.rectangle(
      x + width + WALL_THICKNESS_PX / 2,
      y + height / 2,
      WALL_THICKNESS_PX,
      height,
      { isStatic: true, label: 'plinko-wall' }
    );
    this.wallBodies.push(leftWall, rightWall);
    this.wallBodyIds.add(leftWall.id);
    this.wallBodyIds.add(rightWall.id);
  }

  private buildBars(boardDef: BoardDef): void {
    boardDef.rows.forEach((row, rowIndex) => {
      const pixelY = this.playRect.y + row.y * this.playRect.height;
      row.bars.forEach((bar, barIndex) => {
        const centerXFrac = bar.x + bar.width / 2;
        const pixelX = this.playRect.x + centerXFrac * this.playRect.width;
        const widthPx = Math.max(8, bar.width * this.playRect.width);

        const image = this.scene.matter.add.image(pixelX, pixelY, textureForKind(bar.kind));
        image.setRectangle(widthPx, BAR_HEIGHT_PX, { isStatic: true, isSensor: true });
        image.setDisplaySize(widthPx, BAR_HEIGHT_PX);
        if (bar.kind === 'mult' && bar.value !== undefined) {
          image.setTint(multBarColor(bar.value));
        }

        const meta: BarMeta = {
          id: barId(rowIndex, barIndex),
          rowIndex,
          barIndex,
          kind: bar.kind,
          value: bar.value,
          baseXFrac: centerXFrac,
          moving: bar.moving,
        };

        const bodyId = (image.body as MatterJS.BodyType).id;
        const runtime: BarRuntime = { bodyId, meta, image, pixelY };
        if (bar.kind === 'mult' && bar.value !== undefined) {
          runtime.label = this.makeLabel(pixelX, pixelY, `×${bar.value}`);
        }
        this.bars.set(bodyId, runtime);
        if (bar.moving) this.movingBars.push(runtime);
      });
    });
  }

  private buildFunnel(funnelHeight: number): void {
    const { x, width } = this.zoneRect;
    const funnelTop = this.playRect.y + this.playRect.height;
    const funnelBody = this.scene.matter.add.rectangle(
      x + width / 2,
      funnelTop + funnelHeight / 2,
      width,
      funnelHeight,
      { isStatic: true, isSensor: true, label: 'plinko-funnel' }
    );
    this.funnelBody = funnelBody;
    this.funnelBodyId = funnelBody.id;

    // vaso receptor + embudo: puramente decorativos, sin física propia.
    // Teñidos con el acento del bioma actual (celeste por defecto sin bioma).
    this.decorGraphics = this.scene.add.graphics();
    this.decorGraphics.lineStyle(3, this.accentColor, 0.6);
    this.decorGraphics.lineBetween(x + 6, funnelTop, x + width / 2 - 14, funnelTop + funnelHeight * 0.8);
    this.decorGraphics.lineBetween(x + width - 6, funnelTop, x + width / 2 + 14, funnelTop + funnelHeight * 0.8);

    this.receiverImage = this.scene.add
      .image(x + width / 2, funnelTop + funnelHeight * 0.85, TEX.cupReceiver)
      .setOrigin(0.5, 0.5)
      .setTint(this.accentColor);
  }

  private makeLabel(x: number, y: number, text: string): Phaser.GameObjects.Text {
    return this.scene.add
      .text(x, y, text, { fontFamily: 'monospace', fontSize: '11px', color: '#1b1030' })
      .setOrigin(0.5);
  }

  /** Metadatos de la barra correspondiente a un body id de Matter, si existe. */
  getBarMeta(bodyId: number): BarMeta | undefined {
    return this.bars.get(bodyId)?.meta;
  }

  isFunnelBody(bodyId: number): boolean {
    return this.funnelBodyId === bodyId;
  }

  isWallBody(bodyId: number): boolean {
    return this.wallBodyIds.has(bodyId);
  }

  /**
   * Al primer toque de una barra 'mystery' se revela su valor: la vista
   * pasa a comportarse (y verse) como una barra 'mult' normal por el resto
   * del vertido.
   */
  revealMysteryBar(bodyId: number, value: number): void {
    const runtime = this.bars.get(bodyId);
    if (!runtime || runtime.meta.value !== undefined) return;
    runtime.meta.value = value;
    runtime.image.setTexture(TEX.barMult);
    runtime.image.setTint(multBarColor(value));
    runtime.label = this.makeLabel(runtime.image.x, runtime.pixelY, `×${value}`);
  }

  /** Aplica la oscilación de las barras `moving` para el instante `elapsedMs`. */
  update(elapsedMs: number): void {
    for (const runtime of this.movingBars) {
      if (!runtime.meta.moving) continue;
      const newXFrac = movingBarOffsetX(runtime.meta.baseXFrac, elapsedMs, runtime.meta.moving);
      const pixelX = this.playRect.x + newXFrac * this.playRect.width;
      runtime.image.setPosition(pixelX, runtime.pixelY);
      runtime.label?.setPosition(pixelX, runtime.pixelY);
    }
  }

  destroy(): void {
    for (const runtime of this.bars.values()) {
      runtime.image.destroy();
      runtime.label?.destroy();
    }
    this.bars.clear();
    this.movingBars = [];
    this.receiverImage?.destroy();
    this.decorGraphics?.destroy();

    // Durante el `shutdown` de la escena el mundo de Matter puede estar ya
    // destruido (null); en ese caso los cuerpos se van con él y no hay que
    // (ni se puede) removerlos manualmente.
    const world = this.scene.matter?.world;
    if (world) {
      if (this.funnelBody) world.remove(this.funnelBody);
      for (const wall of this.wallBodies) world.remove(wall);
    }
    this.wallBodies = [];
    this.wallBodyIds.clear();
  }
}
